/**
 * 환경설정 &gt; 부재중응답 (migration 163, 2026-09-05 사용자 지시).
 *
 * 스위치(기본 적용하지 않음) + 부재기간 + 제목 + 메시지 + 대상(모든 메일 | 저장한 대상에게만).
 *
 * ★★★ **이 탭에는 저장바가 있다** — 앞의 넷과 다르다. 제목·메시지는 **글자를 치는 칸**이라
 *    한 글자마다 서버로 보낼 수 없고, 다섯 칸이 서로 얽혀 있어(켜려면 기간과 메시지가 있어야
 *    한다) **함께 저장돼야** 검사가 성립한다. 정본 P2-1 「반영 시점의 단위는 탭」 그대로다 —
 *    값을 골라 두는 탭이라 저장바를 갖는다.
 *    ⚠ 그래서 **응답 대상 목록만은 즉시 반영**이다(목록은 제출이 아니다). 한 탭 안에 두 시점이
 *      섞이지만, 목록은 **저장바 아래 제 절**로 갈라 세워 어디까지가 「저장」인지가 보이게 했다.
 * ★★★ **대상이 「저장한 대상에게만」일 때만 목록이 보인다**(정본 P2-1 「값에 딸린 칸」).
 * ★★ **못 하는 사정을 미리 말한다** — 보내는 계정(SMTP)이 없으면 회사 밖 사람에게는 못 답한다.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addMailVacationTarget, clearMailVacationTargets, deleteMailVacationTarget,
  getMailVacation, patchMailVacation,
  type MailVacationScope, type MailVacationState, type MailVacationTarget,
} from '../../api/mail';
import { Plus, Trash2 } from '../../icons';
import { Button } from '../../components/shared/Button';
import { DateRangePicker } from '../../components/shared/Calendar/DateRangePicker';
import { Input } from '../../components/shared/Input';
import { Select } from '../../components/shared/Select';
import { Switch } from '../../components/shared/Switch';
import { Table, type TableColumn } from '../../components/shared/Table';
import { Textarea } from '../../components/shared/Input/Textarea';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import { MailAddressRuleModal } from './MailAddressRuleModal';
import { MailConfirmDialog } from './MailConfirmDialog';

interface Draft {
  enabled: boolean;
  from: string | null;
  to: string | null;
  subject: string;
  body: string;
  scope: MailVacationScope;
}

const EMPTY: Draft = { enabled: false, from: null, to: null, subject: '', body: '', scope: 'ALL' };

const draftOf = (s: MailVacationState): Draft => ({
  enabled: s.enabled, from: s.from, to: s.to,
  subject: s.subject ?? '', body: s.body ?? '', scope: s.scope,
});

/** 서버가 준 코드 → 사람 말. */
function messageFor(err: unknown, fallback: MailI18nKey): string {
  const res = (err as { response?: { status?: number; data?: { code?: string } } }).response;
  switch (res?.data?.code) {
    case 'PERIOD_REQUIRED': return tMail('mail.vac.err_period');
    case 'BAD_PERIOD': return tMail('mail.vac.err_bad_period');
    case 'BODY_REQUIRED': return tMail('mail.vac.err_body');
    case 'INVALID_ADDRESS': return tMail('mail.vac.err_addr');
    case 'ADDRESS_EXISTS': return tMail('mail.vac.err_exists');
    default: return tMail(res?.status === 400 ? 'mail.vac.legacy' : fallback);
  }
}

export function MailSettingsVacation() {
  const [base, setBase] = useState<Draft>(EMPTY);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [targets, setTargets] = useState<MailVacationTarget[]>([]);
  const [canSendExternal, setCanSendExternal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [ask, setAsk] = useState<{ row: MailVacationTarget } | { clear: true } | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalErr, setModalErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    const s = await getMailVacation();
    setBase(draftOf(s));
    setDraft(draftOf(s));
    setTargets(s.targets);
    setCanSendExternal(s.canSendExternal);
  }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .catch(() => { if (!cancelled) setNote({ ok: false, text: tMail('mail.vac.load_failed') }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  const set = useCallback(<K extends keyof Draft>(k: K, v: Draft[K]) => {
    setDraft((prev) => ({ ...prev, [k]: v }));
    // 무언가 고치는 순간 「저장됨」을 거둔다 — 안 그러면 고친 뒤에도 저장됐다고 적혀 있다.
    setNote((n) => (n?.ok ? null : n));
  }, []);

  const dirty = useMemo(
    () => (Object.keys(EMPTY) as (keyof Draft)[]).some((k) => draft[k] !== base[k]),
    [draft, base],
  );

  const save = useCallback(async () => {
    setBusy(true);
    setNote(null);
    try {
      await patchMailVacation({
        enabled: draft.enabled,
        from: draft.from,
        to: draft.to,
        subject: draft.subject.trim() || null,
        body: draft.body.trim() || null,
        scope: draft.scope,
      });
      await load();
      setNote({ ok: true, text: tMail('mail.vac.saved') });
    } catch (err) {
      setNote({ ok: false, text: messageFor(err, 'mail.vac.save_failed') });
    } finally {
      setBusy(false);
    }
  }, [draft, load]);

  /** 목록은 제출이 아니다 — 그 자리에서 반영한다. */
  const run = useCallback(async (
    fn: () => Promise<void>, failKey: MailI18nKey, inModal = false,
  ): Promise<boolean> => {
    setBusy(true);
    setModalErr(null);
    try {
      await fn();
      await load();
      return true;
    } catch (err) {
      const msg = messageFor(err, failKey);
      if (inModal) setModalErr(msg); else setNote({ ok: false, text: msg });
      return false;
    } finally {
      setBusy(false);
    }
  }, [load]);

  const columns: ReadonlyArray<TableColumn<MailVacationTarget>> = useMemo(() => [
    { key: 'address', header: tMail('mail.vac.col.address'), rowHeader: true, render: (r) => r.address },
    {
      key: 'manage',
      header: tMail('mail.vac.col.manage'),
      width: '96px',
      render: (r) => (
        <Button
          variant="quiet-danger" size="sm" disabled={busy}
          iconLeft={<Trash2 size={13} aria-hidden />}
          aria-label={tMail('mail.vac.delete_aria').replace('{n}', r.address)}
          onClick={() => setAsk({ row: r })}
        >
          {tMail('mail.vac.delete')}
        </Button>
      ),
    },
  ], [busy]);

  if (loading) return <div className="wc-set__body"><p className="wc-set__hint">…</p></div>;

  return (
    <>
      <div className="wc-set__body">
        <section className="wc-set__sec" aria-labelledby="mvac-h">
          <h2 className="wc-set__sech" id="mvac-h">{tMail('mail.vac.sec.set')}</h2>
          <div className="wc-set__rows">

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.vac.use')}</span>
              <div className="wc-set__ctl">
                {/* 저장바가 있는 화면이지만 이 하나는 **스위치**다 — 「적용하지 않음」이 곧 상태다.
                    저장을 눌러야 반영되는 것은 아래 안내가 말한다. */}
                <Switch
                  checked={draft.enabled}
                  onCheckedChange={(next) => set('enabled', next)}
                  label={tMail('mail.vac.use_label')}
                />
                {!draft.enabled && <p className="wc-set__hint">{tMail('mail.vac.off_hint')}</p>}
                <p className="wc-set__hint">{tMail('mail.vac.once_hint')}</p>
                {!canSendExternal && <p className="wc-set__bad">{tMail('mail.vac.external_none')}</p>}
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label" id="mvac-p">{tMail('mail.vac.period')}</span>
              <div className="wc-set__ctl">
                <DateRangePicker
                  className="wc-set__w3"
                  ariaLabel={tMail('mail.vac.period')}
                  value={{ start: draft.from, end: draft.to }}
                  onChange={(next) => setDraft((prev) => ({ ...prev, from: next.start, to: next.end }))}
                  placeholder={tMail('mail.vac.period_ph')}
                />
              </div>
            </div>

            <div className="wc-set__row">
              <label className="wc-set__label" htmlFor="mvac-s">{tMail('mail.vac.subject')}</label>
              <div className="wc-set__ctl">
                <Input
                  id="mvac-s"
                  /* ★ 폭은 **껍데기**에 준다 — `className` 은 안쪽 `<input>` 으로 가서
                     테두리를 그리는 상자가 안 줄어든다(정본 P2-1 「칸이 화면 끝까지 늘어나지 않는다」). */
                  wrapperClassName="wc-set__w3"
                  value={draft.subject}
                  maxLength={500}
                  placeholder={tMail('mail.vac.subject_ph')}
                  onChange={(e) => set('subject', e.target.value)}
                />
                <p className="wc-set__hint">{tMail('mail.vac.subject_hint')}</p>
              </div>
            </div>

            <div className="wc-set__row">
              <label className="wc-set__label" htmlFor="mvac-b">{tMail('mail.vac.body')}</label>
              <div className="wc-set__ctl">
                <Textarea
                  id="mvac-b"
                  wrapperClassName="mvac__body"
                  rows={5}
                  value={draft.body}
                  maxLength={10000}
                  placeholder={tMail('mail.vac.body_ph')}
                  onChange={(e) => set('body', e.target.value)}
                  fullWidth
                />
              </div>
            </div>

            <div className="wc-set__row">
              <span className="wc-set__label">{tMail('mail.vac.scope')}</span>
              <div className="wc-set__ctl">
                <Select
                  className="wc-set__w3"
                  ariaLabel={tMail('mail.vac.scope')}
                  value={draft.scope}
                  onChange={(v) => set('scope', v as MailVacationScope)}
                  options={[
                    { value: 'ALL', label: tMail('mail.vac.scope.all') },
                    { value: 'LISTED', label: tMail('mail.vac.scope.listed') },
                  ]}
                />
              </div>
            </div>
          </div>
        </section>

        {/* 값에 딸린 절 — 「저장한 대상에게만」일 때만 뜻이 있다(정본 P2-1). */}
        {draft.scope === 'LISTED' && (
          <section className="wc-set__sec" aria-labelledby="mvact-h">
            <div className="wc-set__sechead">
              <h2 className="wc-set__sech" id="mvact-h">{tMail('mail.vac.sec.target')}</h2>
              <Button
                variant="primary" size="sm"
                iconLeft={<Plus size={14} aria-hidden />}
                onClick={() => { setModalErr(null); setAddOpen(true); }}
              >
                {tMail('mail.vac.add')}
              </Button>
              <Button
                variant="quiet-danger" size="sm"
                disabled={targets.length === 0 || busy}
                onClick={() => setAsk({ clear: true })}
              >
                {tMail('mail.vac.clear')}
              </Button>
            </div>
            <Table
              caption={tMail('mail.vac.sec.target')}
              columns={columns}
              rows={targets}
              rowKey={(r) => r.id}
              empty={<span>{tMail('mail.vac.target_none')}</span>}
              wrapClassName="mbox__tablewrap"
            />
          </section>
        )}
      </div>

      {/* ══ 저장바 — 바뀐 것이 없으면 잠긴다(정본 P2-1 ④) ══ */}
      <div className="wc-set__foot">
        <p
          className={`wc-set__footmsg${note?.ok ? ' is-ok' : ''}${note?.ok === false ? ' is-bad' : ''}`}
          role={note?.ok === false ? 'alert' : 'status'}
        >
          {note?.text ?? ''}
        </p>
        <Button variant="secondary" onClick={() => { setDraft(base); setNote(null); }} disabled={!dirty || busy}>
          {tMail('mail.spamset.cancel')}
        </Button>
        <Button variant="primary" onClick={() => { void save(); }} disabled={!dirty} loading={busy}>
          {tMail('mail.vac.save')}
        </Button>
      </div>

      {addOpen && (
        <MailAddressRuleModal
          kind="ALLOW"
          title={tMail('mail.vac.target_title')}
          busy={busy}
          error={modalErr}
          onClose={() => { setAddOpen(false); setModalErr(null); }}
          onSubmit={(address) => {
            void (async () => {
              const ok = await run(async () => { await addMailVacationTarget(address); },
                'mail.vac.err_failed', true);
              if (ok) setAddOpen(false);
            })();
          }}
        />
      )}

      <MailConfirmDialog
        open={ask !== null}
        danger
        busy={busy}
        title={ask && 'row' in ask
          ? tMail('mail.vac.del_title').replace('{n}', ask.row.address)
          : tMail('mail.vac.clear_title')}
        description={tMail('mail.vac.clear_desc')}
        confirmLabel={tMail('mail.vac.delete')}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          const cur = ask;
          if (!cur) return;
          void (async () => {
            const ok = await run(async () => {
              if ('row' in cur) await deleteMailVacationTarget(cur.row.id);
              else await clearMailVacationTargets();
            }, 'mail.vac.save_failed');
            if (ok) setAsk(null);
          })();
        }}
      />
    </>
  );
}
